import { useEffect, useState } from "react";
import { takeIdleSignOut } from "@/lib/idle";
import { Navigate, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { errMsg, post } from "@/lib/api";
import { homePathFor } from "@/lib/nav";
import { ArrowRight, Loader2, Eye, EyeOff, KeyRound, Mail, Lock, FlaskConical, Truck, FileCheck2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Btn } from "@/components/common/Panel";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1614308460927-5024ba2e1dcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMG1vZGVybiUyMG1lZGljYWwlMjBsYWJvcmF0b3J5JTIwbWljcm9zY29wZXxlbnwwfHx8fDE3ODY2ODQzMjR8MA&ixlib=rb-4.1.0&q=85";


export default function Login() {
  const { session, login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [idleOut] = useState(() => takeIdleSignOut());
  const [busy, setBusy] = useState(false);

  // The dashboard for every Novus role; a role without it (a partner radiology
  // centre) lands on the first page of its own menu.
  if (session) return <Navigate to={homePathFor(session)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter your work email and password");
    setBusy(true);
    try {
      const s = await login({ email: email.trim(), password });
      toast.success(`Welcome, ${s.name.split(" ")[0]} · ${s.roleLabel}`);
      navigate(homePathFor(s));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-[hsl(var(--background))] lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      {/* ---- the brand panel */}
      <div className="relative hidden overflow-hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#16294F]/95 via-[#1E3A6B]/90 to-[#0C1B38]/95" />
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/[0.06]" />
        <div className="absolute -bottom-16 left-24 h-72 w-72 rounded-full bg-[#13A2AA]/20" />
        <div className="relative flex h-full flex-col justify-between p-14 text-white">
          <img src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="" className="h-12 w-12 rounded-xl bg-white/90 p-1.5" />
          <div>
            <p className="max-w-lg font-display text-[34px] font-bold leading-[1.15] tracking-tight">One platform for the entire diagnostic workflow.</p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/75">Registration, billing, home collection, the lab bench, verification and reporting, in one place, for every hub and center.</p>
            <div className="mt-8 flex flex-wrap items-center gap-1.5" aria-label="Workflow">
              {["Patient", "Order & Bill", "Specimen", "Result", "Verification", "Report"].map((step, i, arr) => (
                <span key={step} className="flex items-center gap-1.5">
                  <span className="rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] font-semibold backdrop-blur">{step}</span>
                  {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-white/50" />}
                </span>
              ))}
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[[FlaskConical, "Lab bench & QC", "Worklists, results, delta checks"], [Truck, "Home collection", "Slots, executives, dispatch"], [FileCheck2, "Reports", "Pathologist-verified, on WhatsApp"]].map(([Icon, t, d]) => (
                <div key={t} className="rounded-xl border border-white/10 bg-white/[0.07] p-3.5 backdrop-blur">
                  <Icon className="h-5 w-5 text-[#8FE8D6]" />
                  <p className="mt-2 text-sm font-semibold">{t}</p>
                  <p className="mt-0.5 text-[11px] leading-snug text-white/65">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* ---- the form */}
      <div className="relative flex flex-col px-6 py-8 sm:px-12 lg:px-16">
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Novus Health Labs" className="h-14 w-auto self-start" data-testid="login-logo" />

          <h1 className="mt-10 font-display text-[28px] font-bold leading-tight tracking-tight">Welcome back</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Sign in to your Novus workspace.</p>
          {idleOut && <p data-testid="idle-signed-out" className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">You were signed out after 30 minutes without activity. Sign in again to continue.</p>}

          <form onSubmit={submit} className="card-elev mt-7 rounded-2xl border border-border bg-card p-6 shadow-[0_18px_40px_-24px_rgba(22,41,79,0.35)] sm:p-7">
            <div className="space-y-4">
              <div>
                <label className="field-label" htmlFor="login-email">Work email</label>
                <div className="relative mt-1.5">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <input id="login-email" data-testid="login-email" type="email" autoComplete="username" autoFocus placeholder="you@novus.com" value={email} onChange={(e) => setEmail(e.target.value)} className="field h-11 pl-10" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="field-label" htmlFor="login-password">Password</label>
                  <button type="button" onClick={() => setForgotOpen(true)} data-testid="login-forgot" className="text-xs font-semibold text-primary hover:underline">Forgot password?</button>
                </div>
                <div className="relative mt-1.5">
                  <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <input id="login-password" data-testid="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="field h-11 pl-10 pr-11" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} data-testid="login-toggle-password"
                    className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <button data-testid="login-submit" type="submit" disabled={busy}
              className="btn-primary-grad group mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold text-primary-foreground transition-all active:scale-[0.99] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
            </button>
          </form>
          <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} initialEmail={email} onDone={(em) => { setEmail(em); setPassword(""); }} />

          <p className="mt-6 text-center text-xs text-muted-foreground">Trouble signing in? Ask your administrator under <span className="font-medium text-foreground">Users &amp; Roles</span>.</p>
        </div>
        <p className="mt-8 text-center text-[11px] text-muted-foreground">© {new Date().getFullYear()} Novus Health Labs · Enterprise diagnostic platform</p>
      </div>

    </div>
  );
}

// Forgot password: the work email, then a 6-digit code texted to the mobile on
// the account and the new password. An account with no mobile on file is
// pointed at a Super Admin, who can set a password under Users & Roles.
function ForgotPasswordDialog({ open, onClose, initialEmail, onDone }) {
  const [step, setStep] = useState("email"); // email -> code, or nomobile
  const [email, setEmail] = useState(initialEmail || "");
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStep("email"); setEmail(initialEmail || ""); setInfo(null); setCode(""); setPw(""); setPw2(""); setShow(false); } }, [open, initialEmail]);

  const send = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter your work email");
    setBusy(true);
    try {
      const r = await post("/auth/forgot-password", { email: email.trim() });
      if (r.sent === false) { setStep("nomobile"); return; }
      setInfo(r); setStep("code"); if (r.devCode) setCode(r.devCode);
      toast.success(r.mobile ? `Code sent to ${r.mobile}` : "If that account exists, a code has been sent to its mobile");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (code.trim().length !== 6) return toast.error("Enter the 6-digit code from the SMS");
    if (pw.length < 8) return toast.error("Use at least 8 characters");
    if (pw !== pw2) return toast.error("The two passwords do not match");
    setBusy(true);
    try {
      await post("/auth/reset-password", { email: email.trim(), code: code.trim(), newPassword: pw });
      toast.success("Password updated. Sign in with the new one.");
      onDone?.(email.trim()); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="forgot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><KeyRound className="h-4 w-4 text-primary" /> Reset your password</DialogTitle>
          <DialogDescription>
            {step === "email" && "Enter your work email. A 6-digit code goes to the mobile number on your account."}
            {step === "code" && `Code sent to ${info?.mobile || "your mobile"}${info?.expiresInMinutes ? ` · valid for ${info.expiresInMinutes} minutes` : ""}.`}
            {step === "nomobile" && "This account has no mobile number on file, so a code cannot be sent."}
          </DialogDescription>
        </DialogHeader>

        {step === "email" && (
          <div>
            <label className="field-label" htmlFor="forgot-email">Work Email</label>
            <input id="forgot-email" data-testid="forgot-email" type="email" autoComplete="username" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} className="field mt-1.5" />
          </div>
        )}

        {step === "code" && (
          <div className="space-y-3">
            {info?.devCode && <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="forgot-devcode">Development build: the SMS gateway is a stub, so the code is <span className="font-mono font-semibold text-foreground">{info.devCode}</span>.</p>}
            <div>
              <label className="field-label" htmlFor="forgot-code">6-digit code</label>
              <input id="forgot-code" data-testid="forgot-code" inputMode="numeric" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="field mt-1.5 font-mono tracking-[0.3em]" />
            </div>
            <div>
              <label className="field-label" htmlFor="forgot-pw">New password</label>
              <div className="relative mt-1.5">
                <input id="forgot-pw" data-testid="forgot-password" type={show ? "text" : "password"} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="field pr-11" />
                <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">At least 8 characters.</p>
            </div>
            <div>
              <label className="field-label" htmlFor="forgot-pw2">Confirm new password</label>
              <input id="forgot-pw2" data-testid="forgot-password2" type={show ? "text" : "password"} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") reset(); }} className="field mt-1.5" />
            </div>
          </div>
        )}

        {step === "nomobile" && (
          <p className="text-sm text-muted-foreground">Ask a Super Admin to set a new password for you under <span className="font-medium text-foreground">Users &amp; Roles</span>. Once your mobile number is on your account, you can reset it yourself from here.</p>
        )}

        <DialogFooter>
          {step === "email" && <><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" data-testid="forgot-send" disabled={busy} onClick={send}>{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send code</Btn></>}
          {step === "code" && <><Btn disabled={busy} onClick={send}>Resend code</Btn><Btn variant="primary" data-testid="forgot-reset" disabled={busy} onClick={reset}>{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Set new password</Btn></>}
          {step === "nomobile" && <Btn variant="primary" onClick={onClose}>Close</Btn>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
